/**
 * Allowed PARENT origins for embedded (iframe) SSO.
 *
 * When Pathway is hosted inside a trusted parent application (e.g. SharePoint), it
 * receives authentication via `postMessage`. Every INBOUND message must originate from an
 * origin in this allow-list, otherwise it is dropped. This is the linchpin of the embedded
 * auth security model: react-auth trusts whatever token Pathway hands it, so Pathway must
 * prove the token came from a trusted parent. Never widen this to "*".
 *
 * The allow-list is intentionally empty by default (secure by default). Configure the real
 * SharePoint origin per environment via VITE_EMBED_PARENT_ORIGINS, a comma-separated list
 * of absolute origins, e.g. "https://contoso.sharepoint.com,https://contoso-admin.sharepoint.com".
 */

import { logger } from "@/telemetry";

/**
 * Parse the comma-separated VITE_EMBED_PARENT_ORIGINS env var into a list of validated,
 * normalised origins. `event.origin` is always a bare origin (scheme + host + optional
 * port, no path/trailing slash), so any entry that is not a bare origin would silently
 * never match. We validate with the URL parser, normalise to `url.origin`, and warn on
 * (then skip) malformed entries so misconfiguration surfaces at startup instead of as
 * mysterious auth failures in production.
 */
const parseEnvOrigins = (): string[] => {
  const raw = import.meta.env.VITE_EMBED_PARENT_ORIGINS;

  if (!raw || typeof raw !== "string") {
    return [];
  }

  const origins: string[] = [];

  for (const entry of raw.split(",")) {
    const trimmed = entry.trim();

    if (!trimmed) continue;

    try {
      const url = new URL(trimmed);

      if (url.pathname !== "/" || url.search !== "" || url.hash !== "") {
        logger.warn(
          "[embedded-auth] ignoring VITE_EMBED_PARENT_ORIGINS entry that is not a bare origin",
          { entry: trimmed },
        );
        continue;
      }

      origins.push(url.origin);
    } catch {
      logger.warn("[embedded-auth] ignoring unparseable VITE_EMBED_PARENT_ORIGINS entry", {
        entry: trimmed,
      });
    }
  }

  return origins;
};

/**
 * Built-in origins. Only the local test harness is included so embedded mode can be
 * exercised in development without configuration. Production SharePoint hosts MUST be
 * supplied via VITE_EMBED_PARENT_ORIGINS.
 */
const DEFAULT_PARENT_ORIGINS: readonly string[] = import.meta.env.DEV
  ? ["http://localhost:5174"]
  : [];

/** All allowed parent origins (built-in + environment). */
export const ALLOWED_PARENT_ORIGINS: readonly string[] = [
  ...DEFAULT_PARENT_ORIGINS,
  ...parseEnvOrigins(),
];

/**
 * Validate an inbound message origin against the allow-list.
 *
 * @param origin - The `event.origin` from a received `MessageEvent`.
 * @returns true only if the origin is explicitly allow-listed.
 */
export function isAllowedParentOrigin(origin: string): boolean {
  if (!origin) {
    return false;
  }

  return ALLOWED_PARENT_ORIGINS.includes(origin);
}
